/**
 * Detailed Case Studies for Quantum Star Marketing.
 *
 * Client identities are referenced by industry + region. Full client names
 * and reference introductions are available for qualified strategic enquiries
 * — book a call to request these.
 */
const CASE_STUDIES = [
  {
    key: "dtc-skincare-scale",
    eyebrow: "Case Study",
    tag: "DTC / Skincare",
    name: "From AED 720K to AED 5.4M in 11 months",
    tagline: "A UAE-based clean-skincare brand, scaled across Meta + TikTok.",
    chart: {
      type: "line",
      title: "Monthly revenue trajectory (AED)",
      yLabel: "Monthly Revenue",
      xLabel: "Engagement Month",
      peak: 720000,
      peakLabel: "AED 720K / mo",
      points: [
        { x: "M1", y: 65000 },
        { x: "M2", y: 92000 },
        { x: "M3", y: 138000 },
        { x: "M4", y: 184000 },
        { x: "M5", y: 246000 },
        { x: "M6", y: 308000 },
        { x: "M7", y: 392000 },
        { x: "M8", y: 470000 },
        { x: "M9", y: 558000 },
        { x: "M10", y: 642000 },
        { x: "M11", y: 720000 },
      ],
    },
    overview:
      "When this Dubai-founded skincare brand came to us, they were stuck at AED 60-80K monthly revenue, running Meta ads at a 1.9x ROAS with creative made in-house on iPhones. Eleven months later they crossed AED 5.4M cumulative revenue, sustained a 5.8x blended ROAS at AED 850K/mo ad spend, and shipped their first wholesale deal with a regional retailer. The unlock wasn't a hack — it was a creative-led engine plus an AI bid layer running across Meta, TikTok, and a rebuilt funnel.",
    highlights: [
      "Rebuilt the product page — conversion rate 1.6% → 4.3%",
      "Shipped 30+ creative variants per month (UGC, motion, statics)",
      "Layered TikTok Spark Ads on top of Meta to lower blended CPM",
      "Klaviyo flows pushed 31% of total revenue (was 6%)",
      "AI bid layer reallocated budget hourly across 3 channels",
      "Built a 24K-strong SMS list with 6.8x ROI on broadcast",
      "Negotiated first wholesale placement with a UAE pharmacy chain",
    ],
    highlightsLabel: "What we shipped",
    phases: [
      { day: "Month 1–2", body: "Funnel rebuild + first creative engine batch. ROAS lifted 1.9x → 3.4x." },
      { day: "Month 3–6", body: "TikTok scale + Klaviyo lifecycle. Spend 4x'd, ROAS held at 5.2x+." },
      { day: "Month 7–11", body: "Wholesale + retention compounding. Brand searches up 480%." },
    ],
    phasesLabel: "The 11-month rebuild",
    outcomes: [
      "7.5x revenue lift (AED 720K → AED 5.4M cumulative)",
      "5.8x sustained blended ROAS at scale",
      "42,000 new customers acquired in 11 months",
      "Brand searches up 480% (Google Trends + GSC)",
      "First wholesale deal with regional UAE retailer",
      "Net 35% gross margin maintained throughout scale",
    ],
    outcomesLabel: "P&L outcomes",
    quote: {
      body: "We went from running ads on hope to running a real growth engine. The biggest unlock was that the team moves faster than we ever could — when something stops working, it's already replaced by the time we'd have noticed.",
      attribution: "Founder, UAE DTC skincare brand",
    },
    examples: [
      "Engagement spanned Sep 2024 – Aug 2025",
      "Channels: Meta (primary), TikTok (scale), Klaviyo + SMS (retention)",
      "Geographies: UAE, Saudi, Kuwait, Bahrain",
      "Team: 1 strategist, 2 buyers, 3 creators, 1 retention lead",
    ],
    examplesLabel: "Engagement profile",
  },
  {
    key: "b2b-saas-pipeline",
    eyebrow: "Case Study",
    tag: "B2B SaaS",
    name: "Pipeline up 238% with AI lead routing",
    tagline: "A MENA-focused B2B SaaS, rebuilt from PLG to enterprise.",
    chart: {
      type: "bar",
      title: "Marketing-sourced pipeline (AED, quarterly)",
      yLabel: "Pipeline (AED)",
      xLabel: "Quarter",
      bars: [
        { x: "Q1 24", y: 1900000 },
        { x: "Q2 24", y: 2400000 },
        { x: "Q3 24", y: 3100000 },
        { x: "Q4 24", y: 5400000 },
        { x: "Q1 25", y: 8200000 },
        { x: "Q2 25", y: 12400000 },
        { x: "Q3 25", y: 18200000 },
      ],
    },
    overview:
      "This Saudi-headquartered vertical SaaS (serving logistics + fleet operators across GCC) was sitting on a great product with mediocre go-to-market. Marketing-sourced pipeline was 18% of total, the demo show-up rate was 41%, and CAC was climbing every quarter. We rebuilt the entire top of funnel — SEO programmatic pages, an ABM motion for enterprise accounts, an AI scoring layer to route hot inbound, and a redesigned demo funnel. In 9 months, marketing-sourced pipeline crossed AED 12.8M with CAC down 37%.",
    highlights: [
      "Programmatic SEO: 1,400 pages targeting industry + use-case combos",
      "ABM motion for top-50 enterprise targets (LinkedIn + outbound)",
      "AI lead scoring + auto-routing to sales mobile inside 60 seconds",
      "Demo funnel rebuilt — show-up rate 41% → 71%",
      "Lifecycle nurture: 14 sequences across freemium + sales-led",
      "Webinar engine: 2x/month with avg 280 registrants",
      "Retention dashboard with churn-risk alerts to CS team",
    ],
    highlightsLabel: "What we shipped",
    phases: [
      { day: "Month 1–3", body: "Pipeline audit, ICP redefinition, demo funnel rebuild." },
      { day: "Month 4–6", body: "Programmatic SEO + ABM motion live. Pipeline +120%." },
      { day: "Month 7–9", body: "AI scoring + retention layer. Net retention crosses 112%." },
    ],
    phasesLabel: "The 9-month rebuild",
    outcomes: [
      "Pipeline up 238% (AED 5.4M → AED 18.2M)",
      "MRR up AED 680K in 9 months",
      "CAC down 37% (AED 12,400 → AED 7,800)",
      "1,400+ qualified demos booked",
      "Demo show-up rate 41% → 71%",
      "Net retention 98% → 112%",
    ],
    outcomesLabel: "P&L outcomes",
    quote: {
      body: "Before Quantum Star, our reps spent half their week chasing bad leads. Now the AI layer hands them only the top 30% — and they close at twice the rate. It's the highest-leverage hire we've made.",
      attribution: "VP Sales, MENA logistics SaaS",
    },
    examples: [
      "Engagement spanned Nov 2024 – Aug 2025",
      "Channels: SEO (programmatic), LinkedIn ABM, lifecycle email, webinars",
      "Geographies: KSA, UAE, Egypt, Oman",
      "Team: 1 strategist, 1 SEO lead, 1 ABM operator, 1 RevOps + AI engineer",
    ],
    examplesLabel: "Engagement profile",
  },
];

export default CASE_STUDIES;
